import { useRef, useState, useEffect } from "react";
import { FaTimes, FaWhatsapp, FaDownload, FaSpinner, FaReceipt } from "react-icons/fa";
import { formatMonto } from "../../utils/rentalCalculations";
import { useWhatsappImageShare } from "../../hooks/useWhatsappImageShare";
import ReciboCard from "./ReciboCard";
import MoneyInput from "../MoneyInput";
import ModalPortal from "../ModalPortal";

const MESES = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

const slugify = (str) =>
  String(str || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const periodoDefault = (mes, anio) => (mes ? `${MESES[mes - 1]} ${anio}` : "");

// Modal con el recibo de cobro de alquiler, totalmente editable y compartible
// por WhatsApp. Los datos llegan precargados (pago + config) pero se pueden
// ajustar antes de enviar.
export default function ReciboShareModal({ data, onClose }) {
  const cardRef = useRef(null);
  const [form, setForm] = useState({});

  // Inicializa / resincroniza el formulario cuando cambia el recibo abierto.
  useEffect(() => {
    if (!data) return;
    setForm({
      numero: "",
      fecha: data.fecha || "",
      nombre: data.nombre || "",
      domicilio: data.domicilio || "",
      localidad: data.localidad || "",
      cuit: data.cuit || "",
      periodo: periodoDefault(data.mes, data.anio),
      propiedad: data.propiedad || "",
      monto: data.monto ?? "",
      observaciones: data.observaciones || "",
    });
  }, [data]);

  const moneda = data?.moneda;
  const fileName = `Recibo-${slugify(form.nombre)}-${slugify(form.periodo)}.png`;
  const primerNombre = String(form.nombre || "").trim().split(/\s+/).slice(-1)[0];

  const mensaje = [
    `Hola${primerNombre ? ` ${primerNombre}` : ""}!`,
    ``,
    `Te paso el comprobante de pago del alquiler${form.propiedad ? ` de ${form.propiedad}` : ""}.`,
    form.periodo ? `Mes: ${form.periodo}` : "",
    `Total: ${formatMonto(form.monto, moneda)}`,
    ``,
    `¡Gracias!`,
    `Inmobiliaria Plorutti`,
  ]
    .filter(Boolean)
    .join("\n");

  const { generating, feedback, waReady, setWaReady, abrirWhatsapp, descargar, compartir } = useWhatsappImageShare({
    cardRef,
    fileName,
    telefono: data?.telefono,
    caption: mensaje,
  });

  // Al editar cualquier campo, la imagen copiada queda vieja: volvemos a pedir
  // copiar antes de abrir WhatsApp.
  useEffect(() => {
    setWaReady(false);
  }, [form, setWaReady]);

  if (!data) return null;

  const { firmaImg, aclaracion } = data;
  const set = (campo) => (e) => setForm({ ...form, [campo]: e.target.value });

  const labelCls = "block text-[11px] font-medium text-gray-500 dark:text-gray-400 mb-1";
  const inputCls =
    "w-full border border-gray-300 dark:border-gray-600 rounded-lg px-2.5 py-1.5 text-xs bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent";

  return (
    <ModalPortal>
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 px-4 py-6 overflow-y-auto">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-4xl my-auto">
          <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-base font-semibold text-gray-800 dark:text-gray-100 inline-flex items-center gap-2">
              <FaReceipt className="text-primary" /> Recibo de pago
            </h3>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <FaTimes />
            </button>
          </div>

          <div className="p-4 max-h-[75vh] overflow-y-auto bg-gray-50 dark:bg-gray-900/30 grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Campos editables */}
            <div className="grid grid-cols-2 gap-2.5 content-start">
              <div>
                <label className={labelCls}>N° de recibo</label>
                <input type="text" value={form.numero || ""} onChange={set("numero")} className={inputCls} placeholder="Ej: 001-06455" />
              </div>
              <div>
                <label className={labelCls}>Fecha</label>
                <input type="date" value={form.fecha || ""} onChange={set("fecha")} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Nombre y Apellido</label>
                <input type="text" value={form.nombre || ""} onChange={set("nombre")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Domicilio</label>
                <input type="text" value={form.domicilio || ""} onChange={set("domicilio")} className={inputCls} placeholder="Inmobiliaria" />
              </div>
              <div>
                <label className={labelCls}>Localidad</label>
                <input type="text" value={form.localidad || ""} onChange={set("localidad")} className={inputCls} placeholder="Inmobiliaria" />
              </div>
              <div>
                <label className={labelCls}>CUIT</label>
                <input type="text" value={form.cuit || ""} onChange={set("cuit")} className={inputCls} placeholder="Opcional" />
              </div>
              <div>
                <label className={labelCls}>Mes / Período</label>
                <input type="text" value={form.periodo || ""} onChange={set("periodo")} className={inputCls} placeholder="Ej: Junio 2026" />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Inmueble sito en</label>
                <input type="text" value={form.propiedad || ""} onChange={set("propiedad")} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Total</label>
                <MoneyInput value={form.monto} onChange={(v) => setForm({ ...form, monto: v })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Observaciones</label>
                <input type="text" value={form.observaciones || ""} onChange={set("observaciones")} className={inputCls} placeholder="Opcional" />
              </div>
            </div>

            {/* Vista previa del recibo (lo que se captura) */}
            <div className="flex justify-center overflow-x-auto self-start md:sticky md:top-0">
              <ReciboCard
                ref={cardRef}
                nombre={form.nombre}
                propiedad={form.propiedad}
                periodo={form.periodo}
                monto={form.monto}
                moneda={moneda}
                fecha={form.fecha}
                observaciones={form.observaciones}
                numero={form.numero}
                domicilio={form.domicilio}
                localidad={form.localidad}
                cuit={form.cuit}
                firmaImg={firmaImg}
                aclaracion={aclaracion}
              />
            </div>
          </div>

          {feedback && (
            <div
              className={`mx-4 mb-2 mt-3 rounded-lg px-3 py-2 text-xs ${
                feedback.type === "success"
                  ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                  : feedback.type === "info"
                  ? "bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400"
                  : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"
              }`}
            >
              {feedback.msg}
            </div>
          )}

          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
              Cerrar
            </button>
            <button
              onClick={descargar}
              disabled={generating}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-lg disabled:opacity-50"
            >
              {generating ? <FaSpinner className="w-3.5 h-3.5 animate-spin" /> : <FaDownload className="w-3.5 h-3.5" />}
              Descargar
            </button>
            {waReady ? (
              <button
                onClick={abrirWhatsapp}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg"
                title="Abre la app de WhatsApp o, si no está, WhatsApp Web"
              >
                <FaWhatsapp className="w-4 h-4" /> Abrir WhatsApp
              </button>
            ) : (
              <button
                onClick={compartir}
                disabled={generating}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg disabled:opacity-50"
                title="Compartir el recibo por WhatsApp (adjunta o copia la imagen)"
              >
                {generating ? <FaSpinner className="w-3.5 h-3.5 animate-spin" /> : <FaWhatsapp className="w-4 h-4" />}
                Enviar por WhatsApp
              </button>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}
